import { Typography, Button } from "@mui/material";
import AlertDialog from "../../components/alert_dialog";
import { useEffect, useState } from "react";
import OTPInput from "otp-input-react";
import { handleLogout } from "../../utilities/common";
import { ApiService, useApi, useSnackbar } from "../../utilities";
import { useQueryClient } from "@tanstack/react-query";
import { emailRegExp, phoneRegExp } from "../../utilities/regex";
import cookieStorage from "../../utilities/cookie_storage";

export const LockScreen = ({ onUnlock }) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const userName = cookieStorage.getItem("user_name");
  const userID = cookieStorage.getItem("userId");
  const userType = cookieStorage.getItem("userType");

  const [lockPin, setLockPin] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState("");
  const [resetPinPayload, setResetPinPayload] = useState(null);

  const [enableOTP, setEnableOTP] = useState(false);
  const [OTP, setOTP] = useState(null);
  const [email_PhoneNo, setEmail_PhoneNo] = useState('');
  const [timer, setTimer] = useState(0);
  const { action: requestOTP, loading: otpLoading } = useApi(
    ApiService.requestOTP,
    {
      success: true,
      error: true,
      noStore: true,
      onComplete: (data, error) => {
        if (!error) {
          setEnableOTP(true)
          setError('')
          setStep('otp_verify')
          setTimer(60); // Start 1-minute timer on successful OTP request
        }
      },
      onError() {
        setEnableOTP(false)
      }
    }
  );

  const {
    action: validateOTP,
    loading: validateOTPLoading,
  } = useApi(ApiService.validateOTP, {
    error: true,
    noStore: true,
    onComplete() {
      setEnableOTP(false);
      setStep('reset_pin');
      setTimer(0);
      setOTP(null)
      setError('')
    }
  });

  const {
    action: resetPin,
    loading: resetPinLoading,
  } = useApi(ApiService.resetLockPin, {
    error: true,
    noStore: true,
    onComplete() {
      showSnackbar("Pin reset successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["getUserById", userID, userType] });
      reset()
    }
  });

  const reset = () => {
    setOTP(null);
    setError('');
    setEnableOTP(false);
    setStep('');
    setResetPinPayload(null);
    setEmail_PhoneNo('');
    setLockPin('')
  }

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleUnlock = async (value) => {
    if (value?.length !== 6) {
      setError("Please enter a valid 6-digit Pin");
      return;
    }
    const response = await ApiService.verifyLockPin({ pin: value, userID })

    if (response?.success) {
      setError("");
      if (onUnlock) onUnlock();
    } else {
      setError("Incorrect Pin");
    }
  };

  const onLogout = () => {
    handleLogout();
  };

  const sendOTP = async () => {

    let obj = { userType, userID, otpType: 'reset-lock-pin' };
    await requestOTP(obj);
    setOTP(null)
  };

  const verifyOTP = async (event) => {
    event.preventDefault();

    let obj = { userType, userID, otp: OTP };
    await validateOTP(obj);
  };

  const resetLockPin = async (event) => {
    event.preventDefault();

    if (resetPinPayload?.newPin?.length !== 6) {
      setError("Please enter a valid 6-digit Pin");
      return;
    }
    if (resetPinPayload?.newPin !== resetPinPayload?.confirmPin) {
      setError("New Pin doesn't match");
      return;
    }

    let obj = { userType, userID, ...resetPinPayload };
    await resetPin(obj);
  };

  return (
    <>

      <AlertDialog
        openDialog={true}
        noActionBtn
        noCancelBtn
        noCloseIcon
        backdropClose
        backdropColor={"grey"}
        dialogActionBtnStyles="w-full flex justify-center items-center"
        children={
          <>
            {
              step === "otp_verify" ?
                <>
                  <Typography fontWeight={500} fontSize={16} className="text-charcoal flex w-full justify-center mb-2">
                    Reset Pin
                  </Typography>
                  {enableOTP ? (
                    <form onSubmit={verifyOTP} className="flex flex-col items-center gap-6 justify-center mt-6 ml-1">
                      <Typography fontWeight={500} fontSize={16} className="text-charcoal flex w-full justify-center mb-2">
                        OTP Verification
                      </Typography>
                      <div className="w-full flex justify-center ms-3">
                        <OTPInput
                          inputStyle={"inputstyle_indv"}
                          value={OTP}
                          onChange={setOTP}
                          autoFocus
                          OTPLength={6}
                          otpType="number"
                          disabled={false}
                        />
                      </div>
                      <Typography
                        variant="body2"
                        onClick={timer > 0 ? null : sendOTP}
                        fontWeight="700"
                        fontSize={"10px"}
                        className={`cursor-pointer self-start my-2 ml-1 ${timer > 0 ? "disabled text-alert-red" : "underline text-primary-blue"}`}
                        sx={{ pointerEvents: timer > 0 ? "none" : "auto", opacity: timer > 0 ? 0.5 : 1 }}
                      >
                        {timer > 0 ? `Resend OTP in ${timer} seconds` : "Resend OTP ?"}
                      </Typography>
                      <Button
                        variant="contained"
                        type='submit'
                        className="save-btn mt-2"
                        disabled={(validateOTPLoading || OTP?.length !== 6)}
                      >
                        <span>{otpLoading ? 'Verifying...' : 'Verify OTP'}</span>
                      </Button>
                    </form>
                  ) : null}

                  {enableOTP ?
                    null : (
                      <Button
                        variant="contained"
                        type='submit'
                        className="save-btn mt-2"
                        disabled={(otpLoading || (!emailRegExp.test(email_PhoneNo) &&
                          !phoneRegExp.test(email_PhoneNo)))}
                      >
                        <span>{otpLoading ? 'Sending...' : 'Send OTP'}</span>
                      </Button>
                    )}

                </>
                : step === "reset_pin" ?
                  <>
                    <Typography fontWeight={500} fontSize={16} className="text-charcoal flex w-full justify-center mb-2">
                      Set New Pin
                    </Typography>
                    <form
                      className="w-full mt-2 flex flex-col items-start gap-6"
                      onSubmit={resetLockPin}>

                      <Typography fontWeight={600} fontSize={12} className="text-charcoal">
                        New Pin
                      </Typography>
                      <OTPInput
                        inputStyle={"inputstyle_indv"}
                        value={resetPinPayload?.newPin}
                        onChange={(val) => {
                          setResetPinPayload((prev) => ({ ...prev, newPin: val }))
                          setError('')
                        }}
                        autoFocus
                        OTPLength={6}
                        otpType="number"
                      />

                      <Typography fontWeight={600} fontSize={12} className="text-charcoal">
                        Re Enter New Pin
                      </Typography>
                      <OTPInput
                        inputStyle={"inputstyle_indv"}
                        value={resetPinPayload?.confirmPin}
                        onChange={(val) => {
                          setResetPinPayload((prev) => ({ ...prev, confirmPin: val }))
                          setError('')
                        }}
                        OTPLength={6}
                        otpType="number"
                      />
                      {error && (
                        <Typography color="error" fontSize={12} className="mt-1">
                          {error}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        type='submit'
                        className="save-btn flex justify-center w-full mt-2"
                        disabled={(resetPinPayload?.newPin?.length !== 6 || resetPinLoading || resetPinPayload?.confirmPin?.length !== 6)}
                      >
                        <span>{resetPinLoading ? 'Saving...' : 'Save Pin'}</span>
                      </Button>
                    </form>
                  </>
                  :
                  <div className="flex flex-col justify-center items-center gap-2 mt-6 mx-6">
                    <Typography fontWeight={500} fontSize={16} className="text-charcoal">
                      Hi, {userName || "User"}
                    </Typography>

                    <Typography fontWeight={600} fontSize={12} className="text-charcoal mt-6">
                      Login
                    </Typography>

                    <form
                      className="w-full mt-2 flex justify-center ms-4"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleUnlock(lockPin);
                      }}
                    >
                      <OTPInput
                        inputStyle={"inputstyle_indv"}
                        value={lockPin}
                        onChange={(value) => {
                          setLockPin(value);
                          if (value.length === 6) {
                            handleUnlock(value);
                          }
                        }}
                        autoFocus
                        OTPLength={6}
                        otpType="number"
                      />
                      <button type="submit" className="hidden" />
                    </form>


                    {error && (
                      <Typography color="error" fontSize={12} className="mt-1">
                        {error}
                      </Typography>
                    )}

                    <Typography className="text-charcoal mt-2">
                      Enter your Pin to unlock
                    </Typography>

                    <Typography
                      className="text-primary-blue text-highlight-hover cursor-pointer mt-6"
                      fontWeight={500}
                      onClick={() => { setError(''); sendOTP(); }}
                    >
                      Forgot Pin? Reset here
                    </Typography>
                    {/* 
                <Typography
                  className="text-primary-blue text-highlight-hover cursor-pointer mt-2"
                  fontWeight={500}
                  onClick={onLogout}
                >
                  Logout
                </Typography> */}
                  </div>

            }
            {step ?
              <Typography
                className="text-primary-blue text-highlight-hover flex justify-center cursor-pointer mt-6"
                fontWeight={500}
                onClick={reset}
              >
                Remember Pin?
              </Typography> : null}

            <Typography
              className="text-primary-blue text-highlight-hover flex justify-center cursor-pointer mt-2"
              fontWeight={500}
              onClick={onLogout}
            >
              Logout
            </Typography>
          </>
        }
      />

    </>
  );
};
